/**
 * Credit Recharge Dialog Component
 *
 * 크레딧 구매를 위한 통합 다이얼로그.
 * - 한국 사용자: PayApp (KRW) - 서버 payrequest + popup 방식
 * - 해외 사용자: Lemon Squeezy (USD)
 */
import { Loader2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useFetcher } from "react-router";

import type { CreditPackage, PaymentConfig } from "../types/packages";

/**
 * PayApp 결제 요청 API 응답 타입
 */
interface PayAppRequestResponse {
  mulNo?: string;
  payurl?: string;
  error?: string;
}

/**
 * Lemon Squeezy 체크아웃 API 응답 타입
 */
interface CheckoutResponse {
  checkoutUrl?: string;
  error?: string;
}

/**
 * Lemon Squeezy 이벤트 데이터 타입
 */
interface LemonSqueezyEventData {
  event: string;
  data?: unknown;
}

/**
 * 전역 SDK 타입 선언
 */
declare global {
  interface Window {
    createLemonSqueezy?: () => void;
    LemonSqueezy?: {
      Url: { Open: (url: string) => void };
      Setup: (options: { eventHandler: (data: LemonSqueezyEventData) => void }) => void;
    };
  }
}

interface CreditRechargeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: PaymentConfig;
  currentCredits?: number;
  requiredCredits?: number;
}

/**
 * 통화별 가격 포맷
 */
function formatPrice(amount: number, currency: "KRW" | "USD"): string {
  if (currency === "KRW") {
    return new Intl.NumberFormat("ko-KR", {
      style: "currency",
      currency: "KRW",
    }).format(amount);
  }
  return `$${amount.toFixed(2)}`;
}

export function CreditRechargeDialog({
  open,
  onOpenChange,
  config,
  currentCredits = 0,
  requiredCredits,
}: CreditRechargeDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // PayApp 결제 요청 fetcher
  const payappFetcher = useFetcher<PayAppRequestResponse>();
  // Lemon Squeezy checkout fetcher
  const fetcher = useFetcher<CheckoutResponse>();
  // PayApp blank-window popup 참조
  const [pendingPopup, setPendingPopup] = useState<Window | null>(null);

  const { packages, isKoreanUser, userId } = config;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (open) {
      setErrorMessage(null);
      // 한국 사용자는 SDK 로딩 불필요 (서버 API 기반)
      if (isKoreanUser) {
        setIsLoading(false);
      }
    }
  }, [open, isKoreanUser]);

  /**
   * Lemon Squeezy SDK 로드 (해외 사용자만)
   */
  useEffect(() => {
    if (!open || isKoreanUser) return;

    if (window.LemonSqueezy) {
      setupLemonSqueezyEventHandler();
      setIsLoading(false);
      return;
    }

    const existingScript = document.querySelector(
      'script[src="https://app.lemonsqueezy.com/js/lemon.js"]'
    );
    if (existingScript) {
      if (window.createLemonSqueezy) {
        window.createLemonSqueezy();
      }
      setupLemonSqueezyEventHandler();
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const script = document.createElement("script");
    script.src = "https://app.lemonsqueezy.com/js/lemon.js";
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.createLemonSqueezy) {
        window.createLemonSqueezy();
      }
      setupLemonSqueezyEventHandler();
      setIsLoading(false);
    };

    script.onerror = () => {
      setIsLoading(false);
    };
  }, [open, isKoreanUser]);

  const setupLemonSqueezyEventHandler = () => {
    window.LemonSqueezy?.Setup({
      eventHandler: (data: LemonSqueezyEventData) => {
        if (data.event === "Checkout.Success") {
          window.location.href = "/payments/success";
        }
      },
    });
  };

  /**
   * PayApp 결제 요청 응답 처리
   *
   * 서버에서 { mulNo, payurl }을 받아 popup으로 결제 페이지를 엽니다.
   * blank-window 패턴: 클릭 시 빈 popup을 먼저 열고, 응답 후 URL 설정.
   */
  useEffect(() => {
    if (!payappFetcher.data) return;

    if (payappFetcher.data.error) {
      setLoadingPlan(null);
      setErrorMessage(payappFetcher.data.error || "결제 준비에 실패했습니다. 다시 시도해주세요.");
      // 대기 중인 popup이 있으면 닫기
      if (pendingPopup) {
        pendingPopup.close();
        setPendingPopup(null);
      }
      return;
    }

    const { payurl } = payappFetcher.data;
    if (!payurl) {
      setLoadingPlan(null);
      return;
    }

    // 사전에 열어둔 blank popup에 URL 설정
    if (pendingPopup && !pendingPopup.closed) {
      pendingPopup.location.href = payurl;
      setPendingPopup(null);
    } else {
      // popup이 차단되었거나 닫힌 경우 → 현재 페이지에서 직접 이동 (모바일 fallback)
      window.location.href = payurl;
    }

    setLoadingPlan(null);
  }, [payappFetcher.data]);

  /**
   * Lemon Squeezy 체크아웃 URL 응답 처리
   */
  useEffect(() => {
    if (fetcher.data?.checkoutUrl) {
      if (window.LemonSqueezy?.Url?.Open) {
        try {
          window.LemonSqueezy.Url.Open(fetcher.data.checkoutUrl);
        } catch {
          window.open(fetcher.data.checkoutUrl, "_blank");
        }
      } else {
        window.open(fetcher.data.checkoutUrl, "_blank");
      }
      setLoadingPlan(null);
    } else if (fetcher.data?.error) {
      setLoadingPlan(null);
      setErrorMessage(fetcher.data.error || "결제 연결에 실패했습니다. 다시 시도해주세요.");
    }
  }, [fetcher.data]);

  /**
   * 패키지 선택 처리
   *
   * PayApp 흐름: 클릭 즉시 blank popup 열기 → 서버에 결제 요청 → payurl로 popup 리디렉트
   * Lemon Squeezy 흐름: variantId를 checkout 엔드포인트로 전송
   */
  const handlePlanClick = (pkg: CreditPackage) => {
    if (loadingPlan || fetcher.state !== "idle" || payappFetcher.state !== "idle") return;

    setErrorMessage(null);
    setLoadingPlan(pkg.id);

    if (isKoreanUser && userId) {
      // PayApp 흐름: blank popup을 동기적으로 열어 브라우저 차단 방지
      const screenLeft = window.screenX ?? window.screenLeft ?? 0;
      const screenTop = window.screenY ?? window.screenTop ?? 0;
      const w = 480;
      const h = 700;
      const left = screenLeft + (window.innerWidth - w) / 2;
      const top = screenTop + (window.innerHeight - h) / 2;
      const popup = window.open(
        "about:blank",
        "payapp_payment",
        `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`,
      );
      if (popup) {
        setPendingPopup(popup);
      }
      // popup이 null이면 차단됨 → useEffect에서 fallback 처리

      // 서버에 결제 요청
      payappFetcher.submit(
        { packageId: pkg.id },
        { method: "POST", action: "/api/payments/payapp/request" }
      );
    } else if (!isKoreanUser) {
      // Lemon Squeezy 흐름
      if (!pkg.variantId) {
        setLoadingPlan(null);
        return;
      }
      fetcher.submit(
        { variantId: pkg.variantId },
        { method: "POST", action: "/api/payments/checkout" }
      );
    }
  };

  const handleClose = () => {
    if (loadingPlan) return;
    onOpenChange(false);
  };

  if (!open || !mounted) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[250] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#F3F3F1]/90 backdrop-blur-md transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative flex max-h-[90vh] w-full max-w-sm animate-[fadeInUp_0.3s_ease-out] flex-col overflow-hidden rounded-[32px] border border-gray-100 bg-white shadow-[0_32px_64px_rgba(0,0,0,0.15)]">
        {/* Close button */}
        <button
          onClick={handleClose}
          disabled={!!loadingPlan}
          className="absolute right-6 top-6 z-10 p-2 text-gray-400 transition-colors hover:text-black disabled:opacity-0"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="p-10 pb-2 text-center">
          <h3 className="mb-2 text-2xl font-black uppercase tracking-tight text-black">
            Purchase Credits
          </h3>
          <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400">
            {requiredCredits
              ? `${requiredCredits} 크레딧 필요 • 현재 ${currentCredits} 크레딧`
              : "크레딧을 구매하여 이미지를 생성하세요"}
          </p>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mx-8 rounded-xl bg-red-50 px-4 py-3 text-center">
            <p className="text-xs font-medium text-red-600">{errorMessage}</p>
          </div>
        )}

        {/* Content */}
        <div className="scrollbar-hide overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-4 py-12">
              <Loader2Icon className="size-8 animate-spin text-gray-400" />
              <span className="text-sm text-gray-400">결제 수단을 불러오는 중...</span>
            </div>
          ) : packages.length === 0 ? (
            <div className="space-y-3 p-8">
              <p className="text-center text-sm text-gray-500">
                결제 상품이 설정되지 않았습니다.
              </p>
              <p className="text-center font-mono text-[10px] text-gray-400">
                관리자에게 문의해주세요.
              </p>
            </div>
          ) : (
            <div className="space-y-3 p-8">
              {packages.map((pkg) => (
                <button
                  key={pkg.id}
                  type="button"
                  onClick={() => handlePlanClick(pkg)}
                  disabled={!!loadingPlan}
                  className="block w-full text-left"
                >
                  <div
                    className={`flex w-full items-center justify-between rounded-2xl border p-5 transition-all ${
                      pkg.popular
                        ? "border-black bg-black text-white"
                        : "border-gray-200 bg-white text-black hover:border-black"
                    } ${loadingPlan === pkg.id ? "ring-2 ring-blue-500" : ""} ${loadingPlan && loadingPlan !== pkg.id ? "opacity-50" : ""}`}
                  >
                    <div>
                      <div
                        className={`mb-1 text-[10px] font-bold uppercase tracking-widest ${
                          pkg.popular ? "text-gray-400" : "text-gray-400"
                        }`}
                      >
                        {pkg.name}
                      </div>
                      <div className="text-xl font-black">{pkg.credits} Credits</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm font-bold">
                        {formatPrice(pkg.price, pkg.currency)}
                      </div>
                      {loadingPlan === pkg.id && (
                        <div className="mt-1 animate-pulse text-[9px] font-bold text-blue-400">
                          결제 연결 중...
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-8 pt-2 text-center">
          <button
            onClick={handleClose}
            disabled={!!loadingPlan}
            className="text-[10px] font-black uppercase tracking-widest text-gray-300 transition-colors hover:text-black disabled:opacity-0"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
